import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReportTemplate from "../../components/reports/ReportTemplate";
import CSVExportButton from "../../components/reports/CSVExportButton";
import { ROUTES } from "../../consts/general";
import { MAX_DISCOUNT_PRICING } from "../../consts/reports";
import { formatCurrencyUsdFixedTwo } from "../../helpers/common";
import { useInitialMountDelay } from "../../helpers/useInitialMountDelay";
import { useAbortableRequest } from "../../helpers/useAbortableRequest";

const COLUMNS = [
  { key: "execution_sku", label: "Execution Sku", type: "string", sortable: true, width: 150 },
  { key: "product_name", label: "Product Name", type: "string", sortable: true, width: 260 },
  { key: "sku", label: "Sku", type: "string", sortable: true, width: 150 },
  { key: "fba_asin", label: "FBA ASIN", type: "string", sortable: false, width: 140 },
  { key: "fbm_asin", label: "FBM ASIN", type: "string", sortable: false, width: 140 },
  { key: "msrp", label: "MSRP", type: "currency", sortable: true, width: 110 },
  { key: "max_discount_pct", label: "Max Discount %", type: "percentage", sortable: false, width: 150 },
  { key: "promo_price", label: "Promo Price", type: "currency", sortable: false, width: 130 },
];

const FILTER_OPTION_KEYS = ["tiers", "categories", "subcategories", "products", "statuses"];

const getTokenHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const appendValues = (params, key, values) => {
  (values || []).forEach((value) => params.append(key, value));
};

const normalizeOptions = (options) => (options || []).map((option) => {
  if (typeof option === "string" || typeof option === "number") {
    return { id: option, value: String(option), label: String(option), name: String(option) };
  }
  return {
    ...option,
    value: String(option.id),
    label: option.name,
  };
});

export default function MaxDiscountPricingReport() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterOptions, setFilterOptions] = useState({
    tiers: [],
    categories: [],
    subcategories: [],
    products: [],
    statuses: [],
  });
  const [filterOptionsResolved, setFilterOptionsResolved] = useState(false);
  const [filterOptionsError, setFilterOptionsError] = useState(false);
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const tierDefaultApplied = useRef(false);
  const pageSize = 50;
  const { beginRequest, isLatest } = useAbortableRequest();
  const {
    beginRequest: beginOptionsRequest,
    isLatest: isLatestOptionsRequest,
  } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const fetchFilterOptions = useCallback(() => {
    const { signal, requestId } = beginOptionsRequest();
    setFilterOptionsError(false);
    fetch(`/api/v1/reports/${MAX_DISCOUNT_PRICING}/filter-options`, {
      headers: getTokenHeaders(),
      signal,
    })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || "Failed to load report filters");
        return body;
      })
      .then((body) => {
        if (!isLatestOptionsRequest(requestId)) return;
        const normalized = FILTER_OPTION_KEYS.reduce((result, key) => ({
          ...result,
          [key]: Array.isArray(body?.[key]) ? normalizeOptions(body[key]) : [],
        }), {});
        setFilterOptions(normalized);
        if (!tierDefaultApplied.current) {
          setSelectedTiers(normalized.tiers
            .filter((option) => option.value !== "NONE")
            .map((option) => option.value));
          tierDefaultApplied.current = true;
        }
        setFilterOptionsResolved(true);
      })
      .catch((requestError) => {
        if (!isLatestOptionsRequest(requestId)) return;
        if (requestError?.name !== "AbortError") {
          setFilterOptionsError(true);
        }
        setFilterOptionsResolved(true);
      });
  }, [beginOptionsRequest, isLatestOptionsRequest]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const subcategoryOptions = useMemo(() => {
    if (selectedCategories.length === 0) return filterOptions.subcategories;
    return filterOptions.subcategories.filter((option) =>
      selectedCategories.includes(String(option.group_id))
    );
  }, [filterOptions.subcategories, selectedCategories]);

  useEffect(() => {
    const validSubcategories = selectedSubcategories.filter((value) =>
      subcategoryOptions.some((option) => String(option.id) === String(value))
    );
    if (validSubcategories.length !== selectedSubcategories.length) {
      setSelectedSubcategories(validSubcategories);
    }
  }, [selectedSubcategories, subcategoryOptions]);

  const query = useMemo(() => {
    const params = new URLSearchParams({
      page: String(currentPage),
      pageSize: String(pageSize),
    });
    appendValues(params, "tierIds", selectedTiers);
    appendValues(params, "categoryIds", selectedCategories);
    appendValues(params, "subcategoryIds", selectedSubcategories);
    appendValues(params, "productGroupIds", selectedProducts);
    appendValues(params, "statuses", selectedStatuses);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (sortField) {
      params.set("sortField", sortField);
      params.set("sortDirection", sortDirection);
    }
    return params.toString();
  }, [
    currentPage,
    debouncedSearch,
    pageSize,
    selectedCategories,
    selectedProducts,
    selectedStatuses,
    selectedSubcategories,
    selectedTiers,
    sortField,
    sortDirection,
  ]);

  const fetchData = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/${MAX_DISCOUNT_PRICING}?${query}`, {
        headers: getTokenHeaders(),
        signal,
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "Failed to load report");
      if (!isLatest(requestId)) return;
      setData(body.data || []);
      setTotalPages(body.totalPages || 1);
    } catch (requestError) {
      if (requestError?.name === "AbortError") return;
      if (!isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
      setTotalPages(1);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [beginRequest, isLatest, query]);

  useEffect(() => {
    if (!initialReady || !filterOptionsResolved) return;
    fetchData();
  }, [fetchData, filterOptionsResolved, initialReady]);

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const formatCell = (row, column) => {
    const value = row?.[column.key];
    if (value == null || value === "") return "";
    if (column.key === "msrp" || column.key === "promo_price") {
      return formatCurrencyUsdFixedTwo(value);
    }
    if (column.key === "max_discount_pct") return `${value}%`;
    return value;
  };

  const filtersConfig = [
    {
      key: "tier-filter",
      options: filterOptions.tiers,
      selectedOptions: selectedTiers,
      onChange: (values) => {
        setSelectedTiers(values);
        setCurrentPage(1);
      },
      placeholder: "Tier",
      renderOption: (option) => <span className="text-gray-900">{option.label}</span>,
    },
    {
      key: "category-filter",
      options: filterOptions.categories,
      selectedOptions: selectedCategories,
      onChange: (values) => {
        setSelectedCategories(values);
        setCurrentPage(1);
      },
      placeholder: "Category",
      renderOption: (option) => <span className="text-gray-900">{option.label}</span>,
    },
    {
      key: "subcategory-filter",
      options: subcategoryOptions,
      selectedOptions: selectedSubcategories,
      onChange: (values) => {
        setSelectedSubcategories(values);
        setCurrentPage(1);
      },
      placeholder: "Sub Category",
      renderOption: (option) => <span className="text-gray-900">{option.label}</span>,
    },
    {
      key: "product-filter",
      options: filterOptions.products,
      selectedOptions: selectedProducts,
      onChange: (values) => {
        setSelectedProducts(values);
        setCurrentPage(1);
      },
      placeholder: "Product",
      renderOption: (option) => <span className="text-gray-900">{option.label}</span>,
    },
    {
      key: "status-filter",
      options: filterOptions.statuses,
      selectedOptions: selectedStatuses,
      onChange: (values) => {
        setSelectedStatuses(values);
        setCurrentPage(1);
      },
      placeholder: "Status",
      renderOption: (option) => <span className="text-gray-900">{option.label}</span>,
    },
  ];

  const renderRows = data.map((row, rowIndex) => (
    <tr key={`${row.execution_sku || "unmapped"}-${row.sku || rowIndex}`} className="divide-x divide-gray-100">
      {COLUMNS.map((column) => (
        <td key={column.key} className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
          {formatCell(row, column)}
        </td>
      ))}
    </tr>
  ));

  const buildExportParams = () => {
    const params = new URLSearchParams({ page: "1", pageSize: "100000" });
    appendValues(params, "tierIds", selectedTiers);
    appendValues(params, "categoryIds", selectedCategories);
    appendValues(params, "subcategoryIds", selectedSubcategories);
    appendValues(params, "productGroupIds", selectedProducts);
    appendValues(params, "statuses", selectedStatuses);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (sortField) {
      params.set("sortField", sortField);
      params.set("sortDirection", sortDirection);
    }
    return params;
  };

  return (
    <ReportTemplate
      title="Max Discount Pricing"
      reportId={MAX_DISCOUNT_PRICING}
      description="Max discount % and promo price by execution SKU, driven by demand tier discount drivers and Protected status"
      breadcrumbs={[{ label: "Reports" }, { label: "" }]}
      columns={COLUMNS}
      data={data}
      isLoading={isLoading}
      error={error}
      onBack={() => navigate(ROUTES.REPORTS)}
      enableSorting
      onSort={handleSort}
      sortField={sortField}
      sortDirection={sortDirection}
      filters={
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search SKU or product"
          className="w-56 rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      }
      filtersConfig={filtersConfig}
      filterNotice={
        filterOptionsError ? (
          <div className="flex items-center gap-2 text-sm text-red-700">
            <span>Filters couldn't load.</span>
            <button
              type="button"
              onClick={fetchFilterOptions}
              className="font-medium text-blue-700 underline hover:text-blue-900"
            >
              Retry
            </button>
          </div>
        ) : null
      }
      exportButton={
        <CSVExportButton
          data={data}
          columns={COLUMNS}
          filename={MAX_DISCOUNT_PRICING}
          onExport={async () => {
            const response = await fetch(
              `/api/v1/reports/${MAX_DISCOUNT_PRICING}/export?${buildExportParams()}`,
              { headers: getTokenHeaders() }
            );
            if (!response.ok) {
              const body = await response.json().catch(() => ({}));
              throw new Error(body.error || "Export failed");
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${MAX_DISCOUNT_PRICING}_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return { success: true, handled: true };
          }}
        />
      }
      pagination={
        totalPages > 1 ? (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        ) : null
      }
      emptyMessage="No pricing rows found."
    >
      {renderRows}
    </ReportTemplate>
  );
}
